import type {
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  RenderTask,
} from "pdfjs-dist";

import {
  GenerationBoundary,
  PdfOperationCancelledError,
  onAbort,
} from "./cancellation";
import { getPdfRuntime } from "./pdf-runtime";
import {
  attachPasswordProvider,
  type PdfPasswordProvider,
} from "./password-controller";

export type PdfLoadProgress = {
  loaded: number;
  total: number | null;
  percent: number | null;
};

type PdfDocumentInitParameters = NonNullable<
  Parameters<(typeof import("pdfjs-dist"))["getDocument"]>[0]
>;

export type PdfDocumentFactory = (
  parameters: PdfDocumentInitParameters,
) => PDFDocumentLoadingTask;

export type OpenPdfOptions = {
  data: Uint8Array | ArrayBuffer;
  passwordProvider?: PdfPasswordProvider;
  onProgress?: (progress: PdfLoadProgress) => void;
  signal?: AbortSignal;
  documentOptions?: Omit<PdfDocumentInitParameters, "data" | "url">;
};

export class PdfDocumentSession {
  readonly generation: number;
  readonly document: PDFDocumentProxy;
  readonly loadingTask: PDFDocumentLoadingTask;

  #boundarySignal: AbortSignal;
  #renderTasks = new Set<RenderTask>();
  #destroyed = false;

  constructor(options: {
    generation: number;
    document: PDFDocumentProxy;
    loadingTask: PDFDocumentLoadingTask;
    boundarySignal: AbortSignal;
  }) {
    this.generation = options.generation;
    this.document = options.document;
    this.loadingTask = options.loadingTask;
    this.#boundarySignal = options.boundarySignal;
  }

  get signal(): AbortSignal {
    return this.#boundarySignal;
  }

  get destroyed(): boolean {
    return this.#destroyed;
  }

  registerRenderTask(task: RenderTask): () => void {
    if (this.#destroyed || this.signal.aborted) {
      task.cancel();
      return () => undefined;
    }

    this.#renderTasks.add(task);
    return () => this.#renderTasks.delete(task);
  }

  isCurrent(generation = this.generation): boolean {
    return (
      !this.#destroyed &&
      !this.signal.aborted &&
      generation === this.generation
    );
  }

  async destroy(): Promise<void> {
    if (this.#destroyed) {
      return;
    }

    this.#destroyed = true;
    for (const task of this.#renderTasks) {
      task.cancel();
    }
    this.#renderTasks.clear();

    await this.loadingTask.destroy();
  }
}

/**
 * Owns exactly one active PDF.js document. Every open advances the generation,
 * aborts stale work, and defensively destroys the previous loading task.
 */
export class PdfLoadManager {
  #boundary = new GenerationBoundary();
  #current: PdfDocumentSession | null = null;
  #pendingTask: PDFDocumentLoadingTask | null = null;
  #documentFactory: PdfDocumentFactory | null;

  constructor(documentFactory: PdfDocumentFactory | null = null) {
    this.#documentFactory = documentFactory;
  }

  get current(): PdfDocumentSession | null {
    return this.#current;
  }

  get generation(): number {
    return this.#boundary.generation;
  }

  async open(options: OpenPdfOptions): Promise<PdfDocumentSession> {
    const previousSession = this.#current;
    const previousPendingTask = this.#pendingTask;
    this.#current = null;
    this.#pendingTask = null;

    const boundary = this.#boundary.advance();

    if (previousPendingTask) {
      await previousPendingTask.destroy().catch(() => undefined);
    }
    if (previousSession) {
      await previousSession.destroy().catch(() => undefined);
    }

    if (options.signal?.aborted || !this.#boundary.isCurrent(boundary.generation)) {
      throw new PdfOperationCancelledError();
    }

    const documentFactory =
      this.#documentFactory ?? (await getPdfRuntime()).getDocument;
    const task = documentFactory({
      ...options.documentOptions,
      data: options.data,
    });
    this.#pendingTask = task;

    if (options.passwordProvider) {
      attachPasswordProvider(task, options.passwordProvider);
    }

    if (options.onProgress) {
      task.onProgress = ({ loaded, total }: { loaded: number; total: number }) => {
        const knownTotal = Number.isFinite(total) && total > 0 ? total : null;
        options.onProgress?.({
          loaded,
          total: knownTotal,
          percent:
            knownTotal === null
              ? null
              : Math.max(0, Math.min(100, Math.round((loaded / knownTotal) * 100))),
        });
      };
    }

    const destroyForExternalAbort = onAbort(options.signal, () => {
      void task.destroy();
    });
    const destroyForGenerationAbort = onAbort(boundary.signal, () => {
      void task.destroy();
    });

    try {
      const document = await task.promise;

      if (
        options.signal?.aborted ||
        !this.#boundary.isCurrent(boundary.generation)
      ) {
        await task.destroy().catch(() => undefined);
        throw new PdfOperationCancelledError();
      }

      const session = new PdfDocumentSession({
        generation: boundary.generation,
        document,
        loadingTask: task,
        boundarySignal: boundary.signal,
      });

      this.#pendingTask = null;
      this.#current = session;
      return session;
    } catch (error) {
      if (
        options.signal?.aborted ||
        boundary.signal.aborted ||
        !this.#boundary.isCurrent(boundary.generation)
      ) {
        throw new PdfOperationCancelledError();
      }
      throw error;
    } finally {
      destroyForExternalAbort();
      destroyForGenerationAbort();
      if (this.#pendingTask === task) {
        this.#pendingTask = null;
      }
    }
  }

  async close(): Promise<void> {
    this.#boundary.advance();

    const pending = this.#pendingTask;
    const current = this.#current;
    this.#pendingTask = null;
    this.#current = null;

    await Promise.all([
      pending?.destroy().catch(() => undefined),
      current?.destroy().catch(() => undefined),
    ]);
  }
}
